import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { UploaderMethodProps } from '#src/app/shared/components/imageUpload';
import { useImageUploadContext } from '#src/app/shared/hooks/imageUpload';

import { createPetitionUpdate } from '../../api/createPetitionUpdate';
import type { CreatePetitionUpdateInput, CreatePetitionUpdateResponse, PetitionUpdateError } from '../../types';

import { uploadImage } from './uploadImage';

type UseGenCreateCallbackProps = {
	startLoading: () => void;
	finishWithError: (submitError?: PetitionUpdateError) => void;
	finishSuccesfully: (response: CreatePetitionUpdateResponse) => void;
};

export function useGenCreateCallback({ startLoading, finishWithError, finishSuccesfully }: UseGenCreateCallbackProps) {
	const {
		state: { image },
	} = useImageUploadContext();
	const utilityContext = useUtilityContext();

	return useCallback(
		async (createPetitionUpdateInput: CreatePetitionUpdateInput, uploader: UploaderMethodProps): Promise<void> => {
			try {
				startLoading();

				let uploadURL;
				if (image) {
					const result = await uploadImage(uploader);
					if (!result.isSuccess) {
						finishWithError(result.error);
						return;
					}
					uploadURL = result.uploadURL;
				}

				const mutationResponse = await createPetitionUpdate(
					{ ...createPetitionUpdateInput, ...(uploadURL && { media: { url: uploadURL } }) },
					utilityContext,
				);

				if (mutationResponse.result === 'SUCCESS') {
					finishSuccesfully(mutationResponse);
				} else if (mutationResponse.result === 'UNAUTHORIZED') {
					finishWithError({
						message: mutationResponse.errorMessage || mutationResponse.result,
						type: mutationResponse.result,
					});
				} else {
					throw Error(mutationResponse.errorMessage || mutationResponse.result);
				}
			} catch (e: unknown) {
				const message = e instanceof Error ? e.message : 'Unexpected error';
				finishWithError({ message, type: 'ERROR' });
			}
		},
		[image, utilityContext, startLoading, finishWithError, finishSuccesfully],
	);
}
